﻿/*****
    Author:	    CT Software Systems, LLC
    Last Modified: 260108 
    Description: Functions to work with jquery

*/

import {
    setModified
} from './ctssVal.js';

import {
    baseURL,
    setAuthHdr
} from './snSite.js';

const getBeforeSendFn = params => 
    params?.useCookie === false
        ? undefined
        : params?.beforeSend ?? setAuthHdr;

/* Show Error functions */

const getErrMsgDivId = params =>
    params?.divErrMsg
        ? getJQueryName({ ctrlNm: params.divErrMsg })
        : '#divErrMsg';

const getMySNApiError = data => {
    if (!data.responseJSON) {
        return 'Unknown error, please email support@mysermonnotes.com with details about what you are trying to do...';
    };
    if (!data.responseJSON.ErrMsg) {
        return data.responseText;
    };
    if (Array.isArray(data.responseJSON.ErrMsg)) {
        if (data.responseJSON.ErrMsg.length === 1) {
            return data.responseJSON.ErrMsg[0];
        }
        return data.responseJSON.ErrMsg.map(err => `${err}\r\n`).join('');
    };
    return data.responseJSON.ErrMsg;
};

const clearMySNError = (params) =>
    $(getErrMsgDivId(params))
        .text('')
        .addClass('hidden');

const showMySNApiError = (params) => {

    let errMsg = getMySNApiError(params.data);
    if (errMsg.indexOf('\r\n') !== -1) {
        errMsg = 'The following errors have occurred:\r\n\n' + errMsg;
    }

    $(getErrMsgDivId(params))
        .text(errMsg)
        .removeClass('hidden');
}

const showMySNError = (params) => 
    $(getErrMsgDivId(params))
        .text(params.errMsg)
        .removeClass('hidden');

/* Ajax database functions */

let isPromiseAllSettled = false;

const allSettled = (params) => {

    const divPromiseAllErrMsg = getErrMsgDivId(params);
    isPromiseAllSettled = true;

    return Promise.allSettled(params.promises)
        .then(results => {
            let errMsg = '';
            results.forEach(result => {
                if (result.status === 'rejected') {
                    errMsg += `${getMySNApiError(result.reason)}\r\n`
                }
            });
            if (errMsg.length > 0) {
                $(divPromiseAllErrMsg)
                    .text(errMsg).removeClass('hidden');
            }
            isPromiseAllSettled = false;
            return results;
        });
}

const deletePromise = (params) => {

    /* controllerNm, actionMethodNm, actionMethodParams will be *
        * deprecated in the future but kept here for legacy code   */

    const url = params.url === undefined
        ? `${baseURL}/api/${params.controllerNm}/${params.actionMethodNm}`
        : `${baseURL}/api/${params.url}`;

    const p = {
        url: url,
        type: 'DELETE'
    };
        
    const beforeSendFn = getBeforeSendFn(params);
    if (beforeSendFn !== undefined) {
        p.beforeSend = beforeSendFn;
    }
    clearMySNError(params);

    showFnStart({ loaderId: params.loaderId, btnId: params.btnId });

    return $.ajax(p)
        .fail((data, status, err) => {
            showMySNApiError({ data: data, divErrMsg: params.divErrMsg })
        })
        .then(() => {
            setModified(false);
        })
        .always(() => {
            showFnEnd({ loaderId: params.loaderId, btnId: params.btnId });
        }); 
}

const getPromise = (params = {}) => {

    const p = {
        url: `${baseURL}/api/${params.url}`,
        type: 'GET'
    };

    const beforeSendFn = getBeforeSendFn(params);
    if (beforeSendFn !== undefined) {
        p.beforeSend = beforeSendFn;
    }

    if (!params.dataType) {
        p.dataType = params.dataType; /* Used only when getting content */
    }

    clearMySNError(params);

    showFnStart({ loaderId: params.loaderId, btnId: params.btnId });

    return $.ajax(p)
            .fail((data, status, err) => {
                if (params?.fnErr) {
                    params.fnErr(data);
                } else if (isPromiseAllSettled === false) {
                    showMySNApiError({ data: data, divErrMsg: params.divErrMsg });
                }
            })
            .always(() => {
                showFnEnd({ loaderId: params.loaderId, btnId: params.btnId });
            });
}

const patchPromise = (params = {}) => {

    if (params.data === undefined) {
        showMySNError({
            divErrMsg: params.divErrMsg,
            errMsg: 'Data is required for patch request...'
        })
        return;
    }

    const data = JSON.stringify(params.data);

    clearMySNError(params);

    showFnStart({ loaderId: params.loaderId, btnId: params.btnId });

    /* processData: false and data: JSON.stringify(patch) 
        overrides jQuery's default serialization for PATCH 
        (which isn't JSON) and forces JSON serialization.   */       

    const p = {
        url: `${baseURL}/api/${params.url}`,
        type: 'PATCH',
        data: data,
        contentType: 'application/json-patch+json'
    };

    const beforeSendFn = getBeforeSendFn(params);
    if (beforeSendFn !== undefined) {
        p.beforeSend = params.beforeSend;
    }

    return $.ajax(p)
        .fail((data, status, err) => {
            if (isPromiseAllSettled === false) {
                showMySNApiError({ data: data, divErrMsg: params.divErrMsg });
            }
        })
        .then(() => {
            setModified(false);
        })
        .always(() => {
            showFnEnd({ loaderId: params.loaderId, btnId: params.btnId });
        });
}

const putPromise = (params = {}) => {

    if (params.data === undefined) {
        showMySNError({
            divErrMsg: params.divErrMsg,
            errMsg: 'Data is required for put request...'
        })
        return;
    }

    const data = JSON.Stringify(params.data);

    clearMySNError(params);

    showFnStart({ loaderId: params.loaderId, btnId: params.btnId });

    const p = {
        url: `${baseURL}/api/${params.url}`,
        type: 'PUT',
        data: data
    };

    const beforeSendFn = getBeforeSendFn(params);
    if (beforeSendFn !== undefined) {
        p.beforeSend = beforeSendFn;
    }

    $(divErrMsg).addClass('hidden');

    return $.ajax(P)
        .fail((data, status, err) => {
            if (isPromiseAllSettled === false) {
                showMySNApiError({ data: data, divErrMsg: params.divErrMsg });
            }
        })
        .then(() => {
            setModified(false);
        })
        .always(() => {
            showFnEnd({ loaderId: params.loaderId, btnId: params.btnId });
        });
}

const savePromise = (params) => {

    /* controllerNm, actionMethodNm, actionMethodParams will be *
        * deprecated in the future but kept here for legacy code   */

    if (params.actionMethodParams === undefined &&
        params.data === undefined) {
        showMySNError({
            divErrMsg: params.divErrMsg,
            errMsg: 'Data or actionMethodParams is required for post request...'
        })
        return;
    }

    const data = params.data === undefined
        ? JSON.stringify(params.actionMethodParams)
        : JSON.stringify(params.data);

    clearMySNError(params);

    const url = params.url === undefined
        ? `${baseURL}/api/${params.controllerNm}/${params.actionMethodNm}`
        : `${baseURL}/api/${params.url}`;

    const p = {
        url: url,
        type: 'POST',
        contentType: 'application/json; charset=utf-8',
        data: data
    }

    const beforeSendFn = getBeforeSendFn(params);
    if (beforeSendFn !== undefined) {
        p.beforeSend = beforeSendFn;
    }

    showFnStart({ loaderId: params.loaderId, btnId: params.btnId });

    return $.ajax(p)
        .fail((data, status, err) => {
            if (params?.fnErr) {
                params.fnErr(data);
            }
            else if (isPromiseAllSettled === false) {
               showMySNApiError({ data: data, divErrMsg: params.divErrMsg });               
            }
        })
        .then((data) => {
            setModified(false);
            return data;
        })
        .always(() => {
            showFnEnd({ loaderId: params.loaderId, btnId: params.btnId });
        });
};

const showFnEnd = (params = {}) => {

    (params.loaderId === undefined)
        ? $('#loader').css('display', 'none')
        : $(getJQueryName({ ctrlNm: params.loaderId })).css('display', 'none');
    if (params.btnId !== undefined) {
        $(getJQueryName({ ctrlNm: params.btnId })).removeAttr('disabled');
    }
};
const showFnStart = (params = {}) => {
    (params.loaderId === undefined)
        ? $('#loader').css('display', 'block')
        : $(getJQueryName({ ctrlNm: params.loaderId })).css('display', 'block');
    if (params.btnId !== undefined) {
        $(getJQueryName({ ctrlNm: params.btnId })).prop('disabled', true);
    }
};

/* Drap and Drop Table Sort */

const getSortKeysFromTableOrder = (params) => {
    const useCamelCase =
        (params.usePascalCase === undefined || params.usePascalCase !== true);
    const json = [];
    let childDisplayOrder, displayOrder;
    if (params.hasChildren === undefined) {
        displayOrder = 1;
        childDisplayOrder = 0;
    } else {
        displayOrder = params.parentDisplayOrder;
        childDisplayOrder = 1;
    }

    $(params.selector).each(function () {
        if (useCamelCase) {
            json.push({
                id: $(this).attr('id'),
                displayOrder: displayOrder,
                childDisplayOrder: childDisplayOrder
            });
        } else {
            json.push({
                ID: $(this).attr('id'),
                DisplayOrder: displayOrder,
                ChildDisplayOrder: childDisplayOrder
            });
        }
        (params.hasChildren === undefined)
            ? displayOrder++
            : childDisplayOrder++;
    });
    return json;
};
const initDragDropTableSort = function (params) {

    let keepTableFormat = function (e, tr) { // Perserves format of table row being dragged to new location
        var $originals = tr.children();
        var $helper = tr.clone();
        $helper.children().each(function (index) {
            $(this).width($originals.eq(index).width())
        });
        return $helper;
    };

    $(params.selector).sortable({
        items: params.items,
        connectswith: undefined,
        handle: (params.handle === undefined) ? '.moveHandle' : params.handle,
        helper: keepTableFormat,
        stop: function (event, ui) { params.fnStop(event, ui); }
    });

};

/* Utility functions */

const autoComplete = (params) => {
    const ctrlNm = (params.ctrlNm[0] === '#') ? params.ctrlNm : '#' + params.ctrlNm;
    $(ctrlNm)
        .autocomplete({ minlength: 0, source: params.source })
        .data("ui-autocomplete")
        ._renderItem = function (ul, item) {
            const newText = String(item.label).replace(
                new RegExp(this.term, "gi"),"<strong>$&</strong>");

            return $("<li></li>")
                .data("item.autocomplete", item)
                .append("<div>" + newText + "</div>")
                .appendTo(ul);
        };
};

/* The following functions are not currently used but may be useful in the future

const disableBtns = (params) =>
    params.selectors.forEach(selector =>
        $(selector).removeClass('disabled').css("pointer-events", "none"));

const enableBtns = (params) =>
    params.selectors.forEach(selector =>
        $(selector).removeClass('disabled').css("pointer-events", "auto"));

const disableSpanIcons = (params) =>
    params.selectors.forEach(selector => $(selector).css("pointer-events", "none"));

const enableSpanIcons = (params) =>
    params.selectors.forEach(selector => $(selector).css("pointer-events", "auto"));

*/

const getJQueryName = (params) => 
    params.ctrlNm[0] === '#' ? params.ctrlNm : '#' + params.ctrlNm;

export {
    clearMySNError, getErrMsgDivId, getMySNApiError,
    showMySNApiError, showMySNError, allSettled,
    deletePromise, getPromise, patchPromise, putPromise, savePromise,
    showFnEnd, showFnStart,
    getSortKeysFromTableOrder, initDragDropTableSort, autoComplete,
    getJQueryName
};

